// src/components/settings/super-admin-tab.jsx
// Super Admin controls, rendered as a tab inside the Settings portal.
//   • Owner  (isSuperAdminOwner)      → edit the cap AND manage who's allowed.
//   • Guest  (id in allowedUserIds)   → edit the cap only.
// The tab is only shown to those users — gating lives in main-settings.jsx.
import { useEffect, useMemo, useState } from "react";
import { Lock } from "lucide-react";
import { roleLabel } from "../../utils/feedHelpers";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  loadBrokerageSettings,
  saveBrokerageSettings,
  BROKERAGE_SETTINGS_FALLBACK,
  isSuperAdminOwner,
} from "../../utils/brokerageSettings";
import {
  backfillOpenHouseListingAgent,
  auditGuessedClosingDates,
  clearGuessedClosingDates,
} from "../../utils/firebasefunctions";
import { formatDate } from "../../utils/helper";
import { useActionDialog } from "../ActionDialog";
import RepairToolsPanel from "./RepairToolsPanel";

export default function SuperAdminTab() {
  const [{ user, users, employees }] = useStateValue();
  const { confirm, dialog } = useActionDialog();

  const [loading, setLoading] = useState(true);
  const [annualCap, setAnnualCap] = useState(
    BROKERAGE_SETTINGS_FALLBACK.annualCap,
  );
  const [capInput, setCapInput] = useState("");
  const [allowedUserIds, setAllowedUserIds] = useState([]);
  const [savingCap, setSavingCap] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  // One-time cleanup: stamp listingAgentId onto historical openHouses docs so
  // agents can see old open houses on listings they own (the host-or-owner
  // visibility rule). Safe to re-run; already-stamped docs are skipped.
  const runOpenHouseBackfill = async () => {
    const ok = await confirm({
      title: "Open house cleanup",
      body: "Run the one-time open house cleanup?",
      note: "Safe to re-run, nothing is deleted.",
      confirmLabel: "Run it",
    });
    if (!ok) return;
    setBackfilling(true);
    try {
      const r = await backfillOpenHouseListingAgent();
      toast.success(`Done. ${r.updated} open house${r.updated === 1 ? "" : "s"} updated.`);
    } catch (err) {
      console.error("Open house backfill failed:", err);
      toast.error("Cleanup did not finish. Try again.");
    } finally {
      setBackfilling(false);
    }
  };

  // ── Guessed closing dates ──
  // Until 2026-08-16 the app stamped "today" into blank closing dates (turn-in
  // seed + Add Deal modal). Audit lists deals whose closing date equals their
  // creation day; clearing is a separate button so the list gets human eyes
  // first — a genuine same-day closing looks identical to a guess.
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [clearing, setClearing] = useState(false);

  const runClosingDateAudit = async () => {
    setAuditing(true);
    try {
      const r = await auditGuessedClosingDates();
      setAuditResult(r);
      if (r.suspects.length === 0) {
        toast.success(`All clear — ${r.scanned} deals scanned, none suspect.`);
      }
    } catch (err) {
      console.error("Closing date audit failed:", err);
      toast.error("Audit did not finish. Try again.");
    } finally {
      setAuditing(false);
    }
  };

  const runClosingDateClear = async () => {
    const n = auditResult?.suspects?.length || 0;
    if (!n) return;
    const ok = await confirm({
      title: "Blank these closing dates?",
      body: `${n} deal${n === 1 ? "" : "s"} will have the closing date cleared. Check the list first — a deal that truly closed the day it was entered looks the same as a guess.`,
      note: "Each deal is re-checked before it's touched. Nothing is deleted.",
      confirmLabel: `Blank ${n} date${n === 1 ? "" : "s"}`,
    });
    if (!ok) return;
    setClearing(true);
    try {
      const r = await clearGuessedClosingDates(
        auditResult.suspects.map((s) => s.id),
      );
      toast.success(
        `Cleared ${r.cleared} closing date${r.cleared === 1 ? "" : "s"}.` +
          (r.skipped ? ` ${r.skipped} skipped (changed since the audit).` : ""),
      );
      // Re-audit so the list on screen reflects what's actually left.
      const fresh = await auditGuessedClosingDates();
      setAuditResult(fresh);
    } catch (err) {
      console.error("Closing date clear failed:", err);
      toast.error("Clearing did not finish. Run the audit again.");
    } finally {
      setClearing(false);
    }
  };

  const isOwner = isSuperAdminOwner(user);

  // Team list for the access card (prefer `users`, fall back to `employees`).
  const team = useMemo(() => {
    const src = (users && users.length ? users : employees) || [];
    return src
      // Only ACTIVE accounts belong in the access list — deleted (type 1)
      // and disabled (type 2) users can't log in anyway, so they'd just be
      // clutter here (Henry's call, 2026-07-28).
      .filter((u) => u && u.id && u.type !== 1 && u.type !== 2)
      .map((u) => ({
        id: u.id,
        name:
          [u.firstname, u.lastname].filter(Boolean).join(" ") ||
          u.name ||
          u.email ||
          "Team Member",
        role: u.role || "",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, employees]);

  useEffect(() => {
    let cancelled = false;
    loadBrokerageSettings().then((s) => {
      if (cancelled) return;
      setAnnualCap(s.annualCap);
      setCapInput(String(s.annualCap));
      setAllowedUserIds(s.allowedUserIds || []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const usd = (n) =>
    "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

  const saveCap = async () => {
    const n = Number(String(capInput).replace(/[^0-9.]/g, ""));
    if (!n || n <= 0) {
      toast.warn("Enter a cap amount greater than 0");
      return;
    }
    setSavingCap(true);
    try {
      await saveBrokerageSettings({ annualCap: n }, user?.id);
      setAnnualCap(n);
      toast.success("Brokerage cap saved");
    } catch (e) {
      toast.error("Couldn't save the cap — try again");
    } finally {
      setSavingCap(false);
    }
  };

  const toggleAccess = async (memberId) => {
    if (!isOwner) return; // only the owner manages the list
    if (memberId === user?.id) return; // can't switch yourself off
    const prev = allowedUserIds;
    // Any save also prunes grants for accounts no longer active (they've
    // been filtered out of `team`), so stale ids clean themselves up.
    const activeIds = new Set(team.map((m) => m.id));
    const pruned = prev.filter((id) => activeIds.has(id));
    const next = pruned.includes(memberId)
      ? pruned.filter((id) => id !== memberId)
      : [...pruned, memberId];
    setAllowedUserIds(next); // optimistic
    try {
      await saveBrokerageSettings({ allowedUserIds: next }, user?.id);
    } catch (e) {
      setAllowedUserIds(prev); // revert on failure
      toast.error("Couldn't update access — try again");
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Lock size={13} /> Private area — visible only to super admins.
      </div>

      {/* Every repair/cleanup tool in one place (they used to be scattered
          across Task Templates and here). */}
      <RepairToolsPanel />

      {/* Brokerage Cap */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-1">
          Brokerage Cap
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          The annual cap each agent works toward. Feeds the dashboard's Progress
          to Cap bar and the split. Current:{" "}
          <b className="text-slate-700">{usd(annualCap)}</b>.
        </p>
        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Cap Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                $
              </span>
              <input
                type="number"
                min="0"
                value={capInput}
                onChange={(e) => setCapInput(e.target.value)}
                className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-[#1E293B]"
                placeholder="70000"
              />
            </div>
          </div>
          <button
            onClick={saveCap}
            disabled={savingCap}
            className="px-5 py-2 text-sm rounded-lg bg-[#1E293B] text-white font-semibold disabled:opacity-60"
          >
            {savingCap ? "Saving…" : "Save Cap"}
          </button>
        </div>
      </div>

      {/* Who Can Access — owner only */}
      {isOwner && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            Who Can Access
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Choose who else can open this Super Admin area. Only you can change
            this list.
          </p>
          <div className="divide-y divide-gray-100">
            {team.map((m) => {
              const isSelf = m.id === user?.id;
              const allowed = isSelf || allowedUserIds.includes(m.id);
              return (
                <div
                  key={m.id}
                  className="flex items-center justify-between py-2.5"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {m.name}
                      {isSelf && (
                        <span className="text-slate-400 font-normal"> · you</span>
                      )}
                    </div>
                    {m.role && (
                      <div className="text-[11px] text-slate-400">
                        {roleLabel(m.role)}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleAccess(m.id)}
                    disabled={isSelf}
                    title={isSelf ? "You always have access" : ""}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      allowed ? "bg-[#1E293B]" : "bg-gray-300"
                    } ${isSelf ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                        allowed ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
            {team.length === 0 && (
              <p className="text-sm text-slate-400 py-3">
                No team members found.
              </p>
            )}
          </div>
        </div>
      )}

      {isOwner && (
      <div className="mt-8 border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-900 mb-1">Open House Cleanup</h3>
        <p className="text-sm text-slate-500 mb-3">
          One-time: makes older open houses visible to the agents who own their
          listings. Safe to run again; it skips anything already fixed.
        </p>
        <button
          type="button"
          onClick={runOpenHouseBackfill}
          disabled={backfilling}
          className="bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-md"
        >
          {backfilling ? "Running..." : "Run Open House Cleanup"}
        </button>
      </div>
      )}

      {/* Guessed Closing Dates — audit first, clear second */}
      {isOwner && (
        <div className="border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold text-slate-900 mb-1">
            Guessed Closing Dates
          </h3>
          <p className="text-sm text-slate-500 mb-3">
            Before 08/16/2026 the app filled blank closing dates with the day
            the deal was entered. This finds deals whose closing date matches
            their creation day so you can review them, then blank the wrong
            ones in one go. A deal that really closed same-day will also show
            up — that's why nothing is cleared until you say so.
          </p>
          <div className="flex items-center gap-3 mb-1">
            <button
              type="button"
              onClick={runClosingDateAudit}
              disabled={auditing || clearing}
              className="bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-md"
            >
              {auditing ? "Scanning..." : "Find Suspect Dates"}
            </button>
            {auditResult && auditResult.suspects.length > 0 && (
              <button
                type="button"
                onClick={runClosingDateClear}
                disabled={auditing || clearing}
                className="bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-md"
              >
                {clearing
                  ? "Clearing..."
                  : `Blank All ${auditResult.suspects.length}`}
              </button>
            )}
          </div>
          {auditResult && (
            <div className="mt-3">
              <p className="text-xs text-slate-400 mb-2">
                Scanned {auditResult.scanned} deals ·{" "}
                {auditResult.suspects.length} suspect
                {auditResult.suspects.length === 1 ? "" : "s"}
              </p>
              {auditResult.suspects.length > 0 && (
                <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
                  {auditResult.suspects.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">
                          {s.propertyaddress}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {s.agentName || "No agent"} · {s.dealStage || "No stage"}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-slate-700 font-medium">
                          {formatDate(s.closingdate)}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          entered {formatDate(s.createdOn)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {dialog}
    </div>
  );
}
